/**
 * pnl-svg.ts — วาดรายงาน P&L เป็น SVG จาก canonical payload (T102)
 *
 * ใช้ payload เดียวกับหน้าจอ/Excel → ตัวเลขตรงกันเสมอ (T077/T105)
 * pure function: ไม่มี DOM/browser API → เทสต์ได้และรันได้ทั้งใน Worker และ browser
 * web แปลง SVG → PNG ด้วย canvas ตอนผู้ใช้กดดาวน์โหลด
 */
import type { PnlLine } from './pnl.js';

export interface PnlSvgReport {
  lines: PnlLine[];
  range: { from: string; to: string };
  completeness: { status: string; missingCostCount: number; affectedRevenue: string };
}

export interface PnlSvgMeta {
  generatedAt: string;
  accountName?: string | undefined;
  businessName?: string | undefined;
  /** ตัวกรองที่ใช้ (แสดงบนภาพ เพื่อให้ไฟล์ที่ส่งออกอธิบายตัวเองได้) */
  filters?: { label: string; value: string }[] | undefined;
}

const WIDTH = 900;
const PAD = 32;
const ROW_H = 26;
const HEADER_H = 150;

const escapeXml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&apos;';
    }
  });

/** จำนวนเงินแบบมี , และทศนิยม 2 ตำแหน่ง (ไม่พึ่ง Intl เพื่อผลลัพธ์คงที่) */
export function formatAmount(value: string): string {
  const negative = value.trim().startsWith('-');
  const abs = negative ? value.trim().slice(1) : value.trim();
  const [intPart = '0', decPart = ''] = abs.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = (decPart + '00').slice(0, 2);
  return `${negative ? '-' : ''}${grouped}.${dec}`;
}

const fmtPct = (p: number | null): string => (p === null ? '—' : `${p.toFixed(1)}%`);

/** สร้าง SVG ของรายงาน (คืน string ที่ฝังใน <img> หรือเขียนเป็นไฟล์ได้) */
export function pnlToSvg(report: PnlSvgReport, meta: PnlSvgMeta): string {
  const incomplete = report.completeness.status !== 'complete';
  const bannerH = incomplete ? 34 : 0;
  const bodyTop = HEADER_H + bannerH;
  const height = bodyTop + report.lines.length * ROW_H + PAD * 2;

  const filterLine = (meta.filters ?? [])
    .map((f) => `${f.label}: ${f.value}`)
    .join('  ·  ');

  const rows = report.lines
    .map((l, i) => {
      const y = bodyTop + i * ROW_H;
      const isHeader = l.kind === 'header';
      const isPct = l.kind === 'percent';
      const emphasis = ['total_income', 'total_costs_of_sale', 'total_sga'].includes(l.key)
        ? 'total'
        : ['gross_margin', 'ebitda', 'ebit', 'profit_before_tax', 'net_profit'].includes(l.key)
          ? 'emph'
          : '';
      const bg =
        isHeader || emphasis === 'total'
          ? `<rect x="${PAD}" y="${y}" width="${WIDTH - PAD * 2}" height="${ROW_H}" fill="#f1f4f9"/>`
          : '';
      const weight = isHeader || emphasis ? '600' : '400';
      const value = isPct
        ? fmtPct(l.percent)
        : l.amount === null
          ? '—'
          : formatAmount(l.amount);
      const negative = !isPct && l.amount !== null && Number(l.amount) < 0;
      const valueCell = isHeader
        ? ''
        : `<text x="${WIDTH - PAD - 8}" y="${y + 18}" text-anchor="end" font-size="14" font-weight="${weight}" fill="${negative ? '#d93636' : '#14181f'}">${escapeXml(value)}</text>`;
      return `${bg}<text x="${PAD + 8}" y="${y + 18}" font-size="14" font-weight="${weight}" fill="#14181f">${escapeXml(l.label)}</text>${valueCell}`;
    })
    .join('');

  const banner = incomplete
    ? `<rect x="${PAD}" y="${HEADER_H - 10}" width="${WIDTH - PAD * 2}" height="28" fill="#fff7e6" stroke="#f0c36d"/>
       <text x="${PAD + 10}" y="${HEADER_H + 9}" font-size="13" fill="#8a5a00">⚠ ข้อมูลยังไม่สมบูรณ์ — SKU ไม่มีต้นทุน ${report.completeness.missingCostCount} รายการ (มูลค่าขายที่กระทบ ${escapeXml(formatAmount(report.completeness.affectedRevenue))})</text>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}" viewBox="0 0 ${WIDTH} ${height}" font-family="'IBM Plex Sans Thai','Noto Sans Thai','Sarabun',system-ui,sans-serif">
  <rect width="${WIDTH}" height="${height}" fill="#ffffff"/>
  <text x="${PAD}" y="${PAD + 14}" font-size="22" font-weight="700" fill="#14181f">รายงานกำไรขาดทุน (P&amp;L)</text>
  <text x="${PAD}" y="${PAD + 40}" font-size="14" fill="#616b78">${escapeXml(meta.accountName ?? '')}${meta.businessName ? ` · ${escapeXml(meta.businessName)}` : ''}</text>
  <text x="${PAD}" y="${PAD + 62}" font-size="14" fill="#14181f">ช่วงวันที่ ${escapeXml(report.range.from)} ถึง ${escapeXml(report.range.to)}</text>
  <text x="${PAD}" y="${PAD + 84}" font-size="12" fill="#616b78">${escapeXml(filterLine)}</text>
  <text x="${WIDTH - PAD}" y="${PAD + 14}" text-anchor="end" font-size="12" fill="#616b78">สร้างเมื่อ ${escapeXml(meta.generatedAt)}</text>
  ${banner}
  ${rows}
</svg>`;
}
